import {
  set as setLocalStorage,
  get as getLocalStorage,
} from 'lib/storage/isomorphicLocalStorage'
import moment from 'moment'
import { ANYWHERE_PLACE_ID, SEARCHED_PLACES_LIMIT, SEARCHED_PLACES_MAX_AGE_MONTHS, SEARCH_STORAGE_KEYS } from 'constants/search'

const MAX_RECOMMENDATION_PLACE_IDS = 10

function pruneExpiredPlaces(places: Array<App.SearchedPlace>): Array<App.SearchedPlace> {
  const cutoffDate = moment().subtract(SEARCHED_PLACES_MAX_AGE_MONTHS, 'months')
  return places.filter(place => moment(place.lastSearched).isAfter(cutoffDate))
}

interface GetSearchedPlacesOptions {
  excludeAnywhere?: boolean
}

export function getSearchedPlaces(options?: GetSearchedPlacesOptions): Array<App.SearchedPlace> {
  const storedPlaces = getLocalStorage(SEARCH_STORAGE_KEYS.SEARCHED_PLACES) as Array<App.SearchedPlace> | null
  if (!storedPlaces) return []
  const pruned = pruneExpiredPlaces(storedPlaces)
  if (options?.excludeAnywhere) return pruned.filter(p => p.placeId !== ANYWHERE_PLACE_ID)
  return pruned
}

export function saveSearchedPlaces(placeIds: Array<string>) {
  const now = new Date().toISOString()
  const existingPlaces = getSearchedPlaces()

  for (const placeId of placeIds) {
    const matchIndex = existingPlaces.findIndex(place => place.placeId === placeId)
    if (matchIndex !== -1) {
      existingPlaces[matchIndex] = {
        ...existingPlaces[matchIndex],
        lastSearched: now,
        searchedCount: existingPlaces[matchIndex].searchedCount + 1,
      }
    } else {
      existingPlaces.push({
        placeId,
        firstSearched: now,
        lastSearched: now,
        searchedCount: 1,
      })
    }
  }

  const sortedAndCapped = existingPlaces
    .toSorted((a, b) => new Date(b.lastSearched).getTime() - new Date(a.lastSearched).getTime())
    .slice(0, SEARCHED_PLACES_LIMIT)

  setLocalStorage(SEARCH_STORAGE_KEYS.SEARCHED_PLACES, sortedAndCapped)
}

export function getRecommendationPlaceIds(): Array<string> {
  return getSearchedPlaces({ excludeAnywhere: true })
    .slice(0, MAX_RECOMMENDATION_PLACE_IDS)
    .map(place => place.placeId)
}

export function getPlaceIdsFromSearchItem(searchItem: App.SearchItem): Array<string> {
  if (searchItem.searchType === 'destination' || searchItem.searchType === 'landmark') {
    return [searchItem.value]
  }

  if (searchItem.searchType === 'searchItemV3') {
    const { extractedData } = searchItem.searchItem
    if ('placeIds' in extractedData && extractedData.placeIds?.length) {
      return extractedData.placeIds
    }
  }

  return []
}
